// frontend/src/components/member-dashboard/components/ProfileSection.js
import React, { useState, useEffect } from "react";
import { profileAPI, stripeAPI } from "../../../services/api";
import { handleApiError } from "../../../utils/errorUtils";
import { FaEdit, FaSave, FaCreditCard, FaUser, FaCamera } from "react-icons/fa";
import EsignStatusIndicator from "../../EsignStatusIndicator";
import "./ProfileSection.css";

/**
 * ProfileSection component for displaying and editing user profile
 * @param {Object} props - Component props
 * @param {Object} props.profile - User profile object
 * @param {Function} props.onProfileUpdate - Function to call when profile is updated
 * @returns {JSX.Element} - Rendered component
 */
const ProfileSection = ({ profile, onProfileUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [profilePicPreview, setProfilePicPreview] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Initialize edited profile when profile prop changes
  useEffect(() => {
    if (profile) {
      setEditedProfile({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        preferredName: profile.preferredName || "",
        contactNumber: profile.contactNumber || "",
        emergencyContactName: profile.emergencyContactName || "",
        emergencyContactPhone: profile.emergencyContactPhone || "",
        street: profile.address?.street || "",
        city: profile.address?.city || "",
        state: profile.address?.state || "",
        zip: profile.address?.zip || "",
        country: profile.address?.country || "",
        birthday: profile.birthday ? profile.birthday.split("T")[0] : "",
        profilePicture: profile.profilePicture || "",
      });
      setProfilePicPreview(profile.profilePicture || "");
    }
  }, [profile]);

  // If profile is not available yet, show loading state
  if (!profile) {
    return (
      <div className="profile-section loading">
        <p>Loading profile information...</p>
      </div>
    );
  }

  // Handle form field changes
  const handleChange = (e) => {
    setEditedProfile({ ...editedProfile, [e.target.name]: e.target.value });
  };

  // Handle profile picture file selection and upload
  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the file immediately
    try {
      setError("");
      const formData = new FormData();
      formData.append("profilePicture", file);

      const uploadResponse = await profileAPI.uploadProfilePicture(formData);

      // Update the profile with the new profile picture URL
      const updatedProfile = {
        ...editedProfile,
        profilePicture: uploadResponse.data.profilePicture,
      };

      // Update state
      setEditedProfile(updatedProfile);

      // Show success message
      setMessage("Profile picture updated successfully!");

      // Call the onProfileUpdate callback to update the parent component
      if (onProfileUpdate) {
        onProfileUpdate(updatedProfile);
      }

      // Show toast notification
      const successMessage = document.createElement("div");
      successMessage.className = "success-toast";
      successMessage.textContent = "Profile picture updated successfully!";
      document.body.appendChild(successMessage);

      // Remove after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
    } catch (err) {
      handleApiError(
        err,
        "ProfileSection.handleProfilePicChange",
        setError,
        "Failed to update profile picture."
      );
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    try {
      // No need to upload profile picture again as it's already uploaded when selected
      const response = await profileAPI.updateProfile(editedProfile);
      setMessage(response.data.message || "Profile updated successfully!");

      // Call the onProfileUpdate callback to update the parent component
      if (onProfileUpdate) {
        onProfileUpdate(editedProfile);
      }

      // Exit editing mode
      setIsEditing(false);

      // Show success message
      const successMessage = document.createElement("div");
      successMessage.className = "success-toast";
      successMessage.textContent =
        response.data.message || "Profile updated successfully!";
      document.body.appendChild(successMessage);

      // Remove after 3 seconds
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 3000);
    } catch (err) {
      handleApiError(
        err,
        "ProfileSection.handleSubmit",
        setError,
        "Failed to update profile."
      );
    }
  };

  // Handle opening Stripe customer portal
  const handleManageBilling = async () => {
    try {
      const response = await stripeAPI.createPortalSession(profile._id);
      window.location.href = response.data.url;
    } catch (err) {
      handleApiError(
        err,
        "ProfileSection.handleManageBilling",
        setError,
        "Error opening billing portal. Please try again."
      );
    }
  };

  return (
    <div className="profile-section">
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      {/* ESIGN-UX-REDESIGN-001 (Story 2 Surface 1) — top-of-profile status row.
          Renders nothing if backend has not yet deployed the additive
          esignStatus block on /api/profile (graceful fallback). */}
      <EsignStatusIndicator esignStatus={profile?.esignStatus} />

      <div className="profile-header">
        <h2>My Profile</h2>
        {!isEditing ? (
          <button
            className="edit-profile-btn"
            onClick={() => setIsEditing(true)}
            aria-label="Edit profile"
          >
            <FaEdit /> Edit
          </button>
        ) : (
          <button
            className="save-profile-btn"
            onClick={handleSubmit}
            aria-label="Save profile"
          >
            <FaSave /> Save
          </button>
        )}
      </div>

      <div className="profile-content">
        <div className="profile-left-column">
          {/* Profile Picture */}
          <div className="profile-picture-container">
            <div className="profile-picture-wrapper">
              {profilePicPreview ? (
                <img
                  src={profilePicPreview}
                  alt="Profile"
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture-placeholder">
                  <FaUser />
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              id="profilePicture"
              name="file"
              accept="image/*"
              onChange={handleProfilePicChange}
              className="file-input"
            />
          </div>

          {/* Button below the profile picture - outside the container and only shown in edit mode */}
          {isEditing && (
            <div className="change-photo-button">
              <label htmlFor="profilePicture" className="upload-label">
                <FaCamera /> Change Profile Picture
              </label>
            </div>
          )}
        </div>

        {/* Profile Details */}
        <div className="profile-details">
          {isEditing ? (
            <form className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={editedProfile.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={editedProfile.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredName">Preferred Name</label>
                  <input
                    type="text"
                    id="preferredName"
                    name="preferredName"
                    value={editedProfile.preferredName}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contactNumber">
                    Contact Number{" "}
                    {!editedProfile.contactNumber && (
                      <span className="required">*</span>
                    )}
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    value={editedProfile.contactNumber}
                    onChange={handleChange}
                    className={
                      !editedProfile.contactNumber ? "highlight-field" : ""
                    }
                    placeholder={
                      !editedProfile.contactNumber
                        ? "Please add your contact number"
                        : ""
                    }
                  />
                  {!editedProfile.contactNumber && (
                    <small className="field-hint">
                      Adding a contact number helps us reach you for important
                      updates
                    </small>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="birthday">Birthdate</label>
                  <input
                    type="date"
                    id="birthday"
                    name="birthday"
                    value={editedProfile.birthday}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="emergency-contact-section">
                <h3>Emergency Contact</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="emergencyContactName">
                      Emergency Contact Name
                      {!editedProfile.emergencyContactName && (
                        <span className="required">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="emergencyContactName"
                      name="emergencyContactName"
                      value={editedProfile.emergencyContactName}
                      onChange={handleChange}
                      className={
                        !editedProfile.emergencyContactName
                          ? "highlight-field"
                          : ""
                      }
                      placeholder={
                        !editedProfile.emergencyContactName
                          ? "Please add an emergency contact name"
                          : ""
                      }
                    />
                    {!editedProfile.emergencyContactName && (
                      <small className="field-hint">
                        Emergency contact information helps us reach someone in
                        case of an emergency
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergencyContactPhone">
                      Emergency Contact Phone
                      {!editedProfile.emergencyContactPhone && (
                        <span className="required">*</span>
                      )}
                    </label>
                    <input
                      type="tel"
                      id="emergencyContactPhone"
                      name="emergencyContactPhone"
                      value={editedProfile.emergencyContactPhone}
                      onChange={handleChange}
                      className={
                        !editedProfile.emergencyContactPhone
                          ? "highlight-field"
                          : ""
                      }
                      placeholder={
                        !editedProfile.emergencyContactPhone
                          ? "Please add an emergency contact phone"
                          : ""
                      }
                    />
                    {!editedProfile.emergencyContactPhone && (
                      <small className="field-hint">
                        Emergency contact phone number for urgent situations
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="address-section">
                <h3>Address</h3>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="street">Street</label>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={editedProfile.street}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={editedProfile.city}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={editedProfile.state}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip">Zip Code</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={editedProfile.zip}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={editedProfile.country}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-section">
                <h3>Personal Information</h3>
                <div className="info-row">
                  <div className="info-label">Name:</div>
                  <div className="info-value">
                    {profile.firstName} {profile.lastName}
                    {profile.preferredName && ` (${profile.preferredName})`}
                  </div>
                </div>

                {profile.contactNumber && (
                  <div className="info-row">
                    <div className="info-label">Contact:</div>
                    <div className="info-value">{profile.contactNumber}</div>
                  </div>
                )}
                {!profile.contactNumber && (
                  <div className="reminder-message">
                    <div className="info-row">
                      <div className="info-label">Contact:</div>
                      <div className="info-value reminder">
                        Please click the Edit Button to add a contact number
                      </div>
                    </div>
                  </div>
                )}

                {profile.birthday && (
                  <div className="info-row">
                    <div className="info-label">Birthdate:</div>
                    <div className="info-value">
                      {new Date(profile.birthday).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>

              <div className="info-section">
                <h3>Emergency Contact</h3>
                {profile.emergencyContactName && (
                  <div className="info-row">
                    <div className="info-label">Name:</div>
                    <div className="info-value">
                      {profile.emergencyContactName}
                    </div>
                  </div>
                )}
                {!profile.emergencyContactName && (
                  <div className="reminder-message">
                    <div className="info-row">
                      <div className="info-label">Name:</div>
                      <div className="info-value reminder">
                        Please click the Edit Button to add emergency contact
                        name
                      </div>
                    </div>
                  </div>
                )}

                {profile.emergencyContactPhone && (
                  <div className="info-row">
                    <div className="info-label">Phone:</div>
                    <div className="info-value">
                      {profile.emergencyContactPhone}
                    </div>
                  </div>
                )}
                {!profile.emergencyContactPhone && (
                  <div className="reminder-message">
                    <div className="info-row">
                      <div className="info-label">Phone:</div>
                      <div className="info-value reminder">
                        Please click the Edit Button to add emergency contact
                        phone
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {(profile.address?.street ||
                profile.address?.city ||
                profile.address?.state) && (
                <div className="info-section">
                  <h3>Address</h3>
                  {profile.address?.street && (
                    <div className="info-row">
                      <div className="info-label">Street:</div>
                      <div className="info-value">{profile.address.street}</div>
                    </div>
                  )}

                  {(profile.address?.city || profile.address?.state) && (
                    <div className="info-row">
                      <div className="info-label">City/State:</div>
                      <div className="info-value">
                        {profile.address?.city}
                        {profile.address?.city &&
                          profile.address?.state &&
                          ", "}
                        {profile.address?.state} {profile.address?.zip}
                      </div>
                    </div>
                  )}

                  {profile.address?.country && (
                    <div className="info-row">
                      <div className="info-label">Country:</div>
                      <div className="info-value">
                        {profile.address.country}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="profile-actions">
        <button className="manage-billing-btn" onClick={handleManageBilling}>
          <FaCreditCard /> Manage Billing
        </button>
      </div>
    </div>
  );
};

export default ProfileSection;
